/**
 * Wind height-map rasterizer.
 *
 * Forma's rapid wind model needs a `HeightMaps` input that the SDK does not
 * provide — it must be computed from terrain and building geometry. This module
 * rasterizes triangle meshes into the two normalized height rasters the model
 * expects: a square grid (default 500x500 at 1.5m/pixel) in row-major order from
 * the north-west corner, with heights normalized to the 0-255 range against a
 * shared min/max. The area of interest sits at the grid center, matching the
 * model's documented convention that results cover only the central window.
 *
 * The function is pure and deterministic so it can be unit-tested on known
 * geometry; it performs no SDK or network access.
 */

import type { FormaHeightMaps } from './index';

const DEFAULT_GRID_SIZE = 500;
const DEFAULT_CELL_SIZE_METERS = 1.5;
/** Inclusion epsilon so cell centers on a triangle edge are not dropped. */
const EDGE_EPSILON = -1e-6;

export interface BuildWindHeightMapsOptions {
  /** Terrain triangle soups (flat [x,y,z,...] arrays, 9 floats per triangle). */
  terrainTriangles: readonly Float32Array[];
  /** Building triangle soups (flat [x,y,z,...] arrays, 9 floats per triangle). */
  buildingTriangles: readonly Float32Array[];
  /** World-space center the grid is built around (e.g. the site centroid). */
  center: { x: number; y: number };
  /** Grid edge length in cells. Defaults to 500 (the model's expected size). */
  size?: number;
  /** Physical length of one cell in meters. Defaults to 1.5. */
  cellSize?: number;
}

interface GridMeta {
  size: number;
  cellSize: number;
  originX: number;
  northY: number;
}

/**
 * Builds normalized terrain and terrain+building height rasters for the rapid
 * wind model. Cells with no terrain coverage fall back to the lowest covered
 * terrain height (a flat baseline) so a partial terrain mesh does not skew the
 * normalization; building heights overlay the terrain via a per-cell maximum.
 */
export function buildWindHeightMaps(options: BuildWindHeightMapsOptions): FormaHeightMaps {
  const size = options.size ?? DEFAULT_GRID_SIZE;
  const cellSize = options.cellSize ?? DEFAULT_CELL_SIZE_METERS;
  const halfExtent = (size * cellSize) / 2;
  const meta: GridMeta = {
    size,
    cellSize,
    originX: options.center.x - halfExtent,
    northY: options.center.y + halfExtent,
  };
  const cellCount = size * size;

  const terrainRaw = new Float64Array(cellCount).fill(Number.NEGATIVE_INFINITY);
  rasterize(options.terrainTriangles, terrainRaw, meta);
  const buildingRaw = new Float64Array(cellCount).fill(Number.NEGATIVE_INFINITY);
  rasterize(options.buildingTriangles, buildingRaw, meta);

  const terrainBaseline = minCovered(terrainRaw) ?? 0;
  const terrain = new Float64Array(cellCount);
  const buildingAndTerrain = new Float64Array(cellCount);

  for (let index = 0; index < cellCount; index += 1) {
    const terrainHeight = isCovered(terrainRaw[index]) ? terrainRaw[index]! : terrainBaseline;
    const buildingHeight = buildingRaw[index]!;
    terrain[index] = terrainHeight;
    buildingAndTerrain[index] = isCovered(buildingHeight)
      ? Math.max(terrainHeight, buildingHeight)
      : terrainHeight;
  }

  const { minHeight, maxHeight } = heightRange(terrain, buildingAndTerrain);

  return {
    terrainHeightArray: normalize(terrain, minHeight, maxHeight),
    buildingAndTerrainHeightArray: normalize(buildingAndTerrain, minHeight, maxHeight),
    minHeight,
    maxHeight,
  };
}

function rasterize(
  triangleArrays: readonly Float32Array[],
  grid: Float64Array,
  meta: GridMeta,
): void {
  for (const triangles of triangleArrays) {
    const triangleCount = Math.floor(triangles.length / 9);

    for (let triangle = 0; triangle < triangleCount; triangle += 1) {
      rasterizeTriangle(triangles, triangle * 9, grid, meta);
    }
  }
}

function rasterizeTriangle(
  triangles: Float32Array,
  offset: number,
  grid: Float64Array,
  meta: GridMeta,
): void {
  const x0 = triangles[offset]!;
  const y0 = triangles[offset + 1]!;
  const z0 = triangles[offset + 2]!;
  const x1 = triangles[offset + 3]!;
  const y1 = triangles[offset + 4]!;
  const z1 = triangles[offset + 5]!;
  const x2 = triangles[offset + 6]!;
  const y2 = triangles[offset + 7]!;
  const z2 = triangles[offset + 8]!;

  // Barycentric denominator; a (near) zero area means a degenerate triangle.
  const denominator = (y1 - y2) * (x0 - x2) + (x2 - x1) * (y0 - y2);
  if (Math.abs(denominator) < 1e-12) {
    return;
  }

  const cols = [worldToCol(x0, meta), worldToCol(x1, meta), worldToCol(x2, meta)];
  const rows = [worldToRow(y0, meta), worldToRow(y1, meta), worldToRow(y2, meta)];
  const minCol = clampIndex(Math.floor(Math.min(...cols)), meta.size);
  const maxCol = clampIndex(Math.ceil(Math.max(...cols)), meta.size);
  const minRow = clampIndex(Math.floor(Math.min(...rows)), meta.size);
  const maxRow = clampIndex(Math.ceil(Math.max(...rows)), meta.size);

  for (let row = minRow; row <= maxRow; row += 1) {
    for (let col = minCol; col <= maxCol; col += 1) {
      const px = meta.originX + (col + 0.5) * meta.cellSize;
      const py = meta.northY - (row + 0.5) * meta.cellSize;
      const w0 = ((y1 - y2) * (px - x2) + (x2 - x1) * (py - y2)) / denominator;
      const w1 = ((y2 - y0) * (px - x2) + (x0 - x2) * (py - y2)) / denominator;
      const w2 = 1 - w0 - w1;

      if (w0 < EDGE_EPSILON || w1 < EDGE_EPSILON || w2 < EDGE_EPSILON) {
        continue;
      }

      const z = w0 * z0 + w1 * z1 + w2 * z2;
      const index = row * meta.size + col;

      if (z > grid[index]!) {
        grid[index] = z;
      }
    }
  }
}

function worldToCol(x: number, meta: GridMeta): number {
  return (x - meta.originX) / meta.cellSize;
}

function worldToRow(y: number, meta: GridMeta): number {
  return (meta.northY - y) / meta.cellSize;
}

function clampIndex(value: number, size: number): number {
  return Math.max(0, Math.min(size - 1, value));
}

function isCovered(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value);
}

function minCovered(grid: Float64Array): number | undefined {
  let min: number | undefined;

  for (const value of grid) {
    if (Number.isFinite(value) && (min === undefined || value < min)) {
      min = value;
    }
  }

  return min;
}

function heightRange(
  terrain: Float64Array,
  buildingAndTerrain: Float64Array,
): { minHeight: number; maxHeight: number } {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const value of terrain) {
    min = Math.min(min, value);
    max = Math.max(max, value);
  }

  for (const value of buildingAndTerrain) {
    min = Math.min(min, value);
    max = Math.max(max, value);
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { minHeight: 0, maxHeight: 0 };
  }

  return { minHeight: min, maxHeight: max };
}

function normalize(grid: Float64Array, minHeight: number, maxHeight: number): number[] {
  const span = maxHeight - minHeight;

  if (span <= 0) {
    return new Array(grid.length).fill(0);
  }

  const normalized = new Array<number>(grid.length);

  for (let index = 0; index < grid.length; index += 1) {
    normalized[index] = Math.round(((grid[index]! - minHeight) / span) * 255);
  }

  return normalized;
}
